// The paint order of a generated section's calls (docs/claude/sections.md, *Sections*):
// farthest tag first, ties in the order made — except where one object's FACE
// (a room's wall face, an extruded polygon's side) or a drawn wall's piece and
// a call of ANOTHER object share area in the view and their plans say exactly
// which stands in front over the columns they share. A tag is one depth per
// call — an object's footprint's nearest point; a face's pieces the depths its
// stack of faces gives them (`occludeFaces`) —, the depth at no column in
// particular: by tags alone a back face seen through a front pane painted over
// an object standing in the room, a slanted face over an object in front of its
// far end, and a drawn wall against the room's back face over the object in
// front of both. Pure and DOM-free (test/sectionOrder.test.mjs,
// test/section.test.mjs).

import type {
  SecCall,
  SecKind,
} from './selfOcclusion';
import type { SecPt } from './section';

/** A recorded call, with its tag, the order it was made in and the group of
 *  its plan (−1: none). */
export interface PaintCall extends SecCall { d: number; i: number; g: number }

/** The calls with one plan: the object they belong to (a number per object),
 *  whether they are pieces of one of its FACES or of a drawn WALL, the plan
 *  (`planOf` reads it), the columns and depths it spans and the least and
 *  greatest of its calls' tags (none yet: ∞, −∞). */
export interface PaintGroup<P> { o: number; face: boolean; wall?: boolean; plan: P; t0: number; t1: number; d0: number; d1: number; z0: number; z1: number }

/** A plan in the section's frame: rings of (t, d) pairs — t along the cut, d
 *  in front of it. A face's is its segment (one ring of two points), then —
 *  of a wall with a thickness — the wall as a ring and its inner line; a
 *  footprint's its rings (a point, a segment or a polygon each). */
export type PlanRings = readonly ArrayLike<number>[];

/** What a verdict's clearance loses when only the plans' nearest depths give
 *  it (`close`), or a face's segment where the surface seen gives none
 *  (`faceVerdict`): more than any clearance (mm). */
const INTERPENETRATING = 1e9;
/** Two depths, or two view coordinates, closer than this are one (mm). */
const EPS = 1e-6;
/** A plan point this close to a column is on it (mm). */
const ON = 1e-7;

/** The work one order may take: BUDGET plus PER_CALL a call, in steps of about
 *  one cost (a pair compared, a waiting call looked at, a step round a cycle)
 *  — the sweep for pairs half of it, each scan all of it, the cycles half.
 *  Past its share, the part of the drawing that spent it keeps its tags. */
const BUDGET = 1 << 13, PER_CALL = 16;
/** How many times the scan starts again without the costliest part's turns,
 *  and how many cycles are broken one turn at a time, before the tags decide. */
const RESTARTS = 2, CYCLES = 1024;

/** How a `SecDraw` member's arguments place it in the view: a box (t0, t1,
 *  h0, h1 first), a vertical edge (t, h0, h1), a closed ring or an open path
 *  of `SecPt`s — and its number in an `OrderKey`. Every member is placed here,
 *  so a new one does not compile until it is. */
type Shape = 'quad' | 'vedge' | 'ring' | 'path';
const KINDS: Record<SecKind, { shape: Shape; code: number }> = {
  profile: { shape: 'quad', code: 1 }, band: { shape: 'quad', code: 2 }, elev: { shape: 'quad', code: 3 }, outline: { shape: 'quad', code: 4 },
  glass: { shape: 'quad', code: 5 }, window: { shape: 'quad', code: 6 }, vedge: { shape: 'vedge', code: 7 }, poly: { shape: 'ring', code: 8 }, line: { shape: 'path', code: 9 },
};

/** Buffers kept between orders (a section is replayed several times per
 *  mousemove): a slot each, grown, never shrunk — and the waiting calls'
 *  nodes (`paintOrder`). */
const INTS: Int32Array[] = [], FLOATS: Float64Array[] = [], NODES: Int32Array[] = [new Int32Array(1024), new Int32Array(1024), new Int32Array(1024)];

/** Slot `k`'s buffer as n copies of `v`. */
function ints(k: number, n: number, v = 0): Int32Array {
  if (!(INTS[k]?.length >= n)) INTS[k] = new Int32Array(Math.max(n, 2 * (INTS[k]?.length ?? 0)));
  const out = INTS[k].subarray(0, n);
  out.fill(v);
  return out;
}

/** Slot `k`'s buffer, at least n long, as it was left. */
function keepInts(k: number, n: number): Int32Array {
  if (!(INTS[k]?.length >= n)) { const w = new Int32Array(Math.max(n, 2 * (INTS[k]?.length ?? 0))); if (INTS[k]) w.set(INTS[k]); INTS[k] = w; }
  return INTS[k];
}

/** Slot `k`'s float buffer, at least n long, as it was left. */
function keepFloats(k: number, n: number): Float64Array {
  if (!(FLOATS[k]?.length >= n)) { const w = new Float64Array(Math.max(n, 2 * (FLOATS[k]?.length ?? 0))); if (FLOATS[k]) w.set(FLOATS[k]); FLOATS[k] = w; }
  return FLOATS[k];
}

/** Slot `k`'s buffer of n floats, as n copies of `v`. */
function floats(k: number, n: number, v = 0): Float64Array {
  if (!(FLOATS[k]?.length >= n)) FLOATS[k] = new Float64Array(Math.max(n, 2 * (FLOATS[k]?.length ?? 0)));
  const out = FLOATS[k].subarray(0, n);
  out.fill(v);
  return out;
}

/** How a call covers the view, for `viewsShare`: its box, with no area (a
 *  vedge, a line), or a ring that is no rectangle. */
const BOXED = 0, NO_AREA = 1, RING = 2;

/** A call's box in the view (t0, t1, h0, h1 at 4·i), written into `box`, and
 *  how it covers it into `cls`. */
function boxInto(box: Float64Array, cls: Int32Array, i: number, { k, a }: SecCall): void {
  let t0: number, t1: number, h0: number, h1: number;
  const shape = KINDS[k].shape;
  cls[i] = shape === 'quad' ? BOXED : shape === 'ring' && ringOf(a[0] as readonly SecPt[]) ? RING : shape === 'ring' ? BOXED : NO_AREA;
  switch (shape) {
    case 'ring': case 'path':
      t0 = h0 = Infinity; t1 = h1 = -Infinity;
      for (const p of a[0] as readonly SecPt[]) { if (p.t < t0) t0 = p.t; if (p.t > t1) t1 = p.t; if (p.h < h0) h0 = p.h; if (p.h > h1) h1 = p.h; }
      break;
    case 'vedge': {
      const t = a[0] as number, ha = a[1] as number, hb = a[2] as number;
      t0 = t1 = t; h0 = Math.min(ha, hb); h1 = Math.max(ha, hb);
      break;
    }
    case 'quad': {
      const ta = a[0] as number, tb = a[1] as number, ha = a[2] as number, hb = a[3] as number;
      t0 = Math.min(ta, tb); t1 = Math.max(ta, tb); h0 = Math.min(ha, hb); h1 = Math.max(ha, hb);
      break;
    }
    default: { const never: never = shape; throw new Error(`no box for ${String(never)}`); }
  }
  box[4 * i] = t0; box[4 * i + 1] = t1; box[4 * i + 2] = h0; box[4 * i + 3] = h1;
}

/** Do two spans share more than a hair — or does one of no width lie strictly inside the other? */
function spansMeet(a0: number, a1: number, b0: number, b1: number): boolean {
  if (Math.min(a1, b1) - Math.max(a0, b0) > EPS) return true;
  if (a1 - a0 <= EPS) return b1 - b0 > EPS && a0 > b0 + EPS && a0 < b1 - EPS;
  return b1 - b0 <= EPS && a1 - a0 > EPS && b0 > a0 + EPS && b0 < a1 - EPS;
}

const boxesMeet = (box: Float64Array, i: number, j: number): boolean =>
  spansMeet(box[4 * i], box[4 * i + 1], box[4 * j], box[4 * j + 1]) && spansMeet(box[4 * i + 2], box[4 * i + 3], box[4 * j + 2], box[4 * j + 3]);

/** What the last verdict found: the least clearance between the two over the
 *  columns it read (mm, 0 where they touch; less `INTERPENETRATING` where only
 *  their nearest depths, or a face's segment, give it). */
let margin = 0;
/** The depths ring `r` reaches at a column, as `reach` found them. */
let rLo = 0, rHi = 0;

/** A span of depths being widened by `widenAt`. */
let spanLo = 0, spanHi = 0;
/** Widen `spanLo` / `spanHi` by the depths ring `w` reaches at column t. */
function widenAt(w: ArrayLike<number>, t: number): void {
  if (reach(w, t)) { if (rLo < spanLo) spanLo = rLo; if (rHi > spanHi) spanHi = rHi; }
}

/** Does ring `r` reach column t? Then `rLo` / `rHi` are the nearest and
 *  farthest depths of its edges there (a ring of one point is that point, of
 *  two the segment). */
function reach(r: ArrayLike<number>, t: number): boolean {
  const n = r.length >> 1, edges = n < 3 ? Math.max(1, n - 1) : n;
  let lo = Infinity, hi = -Infinity;
  for (let e = 0; e < edges; e++) {
    const j = n === 1 ? 0 : (e + 1) % n;
    const pt = r[2 * e], pd = r[2 * e + 1], qt = r[2 * j], qd = r[2 * j + 1];
    if (t < Math.min(pt, qt) - ON || t > Math.max(pt, qt) + ON) continue;
    if (Math.abs(qt - pt) <= ON) { lo = Math.min(lo, pd, qd); hi = Math.max(hi, pd, qd); continue; }
    const d = pd + (qd - pd) * Math.max(0, Math.min(1, (t - pt) / (qt - pt)));
    if (d < lo) lo = d;
    if (d > hi) hi = d;
  }
  rLo = lo; rHi = hi;
  return hi >= lo;
}

/**
 * How a plan stands against a face's segment `seg` (t, d, t, d) over the
 * columns [c0, c1]: 1 wholly in front of it there, −1 wholly behind (touching
 * its line is either), 0 neither — it reaches past the line, lies on it, or
 * nothing of it stands over those columns. With `nearest`, in front where its
 * NEAREST depth stands in front of the line at every column (what of an
 * opaque prism reaches behind is hidden by its own front). Exact: at a column
 * a ring reaches the depths of its edges there, the nearest and farthest of
 * them piecewise linear in t, breaking only at its points' columns (no two
 * edges of a ring cross), so its least clearance to the line is found at
 * those and the ends.
 */
export function planVerdict(seg: ArrayLike<number>, rings: PlanRings, c0: number, c1: number, nearest = false): -1 | 0 | 1 {
  const ta = seg[0], da = seg[1], tb = seg[2], db = seg[3];
  if (!(Math.abs(tb - ta) > EPS) || !(c1 >= c0)) return 0;
  const slope = (db - da) / (tb - ta);
  open();
  for (const r of rings) {
    const n = r.length >> 1;
    lineAt(r, c0, ta, da, slope);
    if (c1 > c0) lineAt(r, c1, ta, da, slope);
    for (let i = 0; i < n && undecided(nearest); i++) { const t = r[2 * i]; if (t > c0 && t < c1) lineAt(r, t, ta, da, slope); }
    if (!undecided(nearest)) return 0;
  }
  return close(nearest);
}

/** Does the last verdict hold over any of its columns as well? Not where the
 *  surface seen gave none and the face's segment decided (`faceVerdict`). */
let exact = true;

/**
 * How a plan stands against a FACE's plan `rs` over the columns: its segment,
 * then, a room's, the wall it is the face of as a ring and the wall's inner
 * line. Against a wall the surface SEEN decides — at each column the wall's
 * nearest depth, its inner face where it is turned away — and the plan's
 * nearest depth there: in front where it stands in front of that at every
 * column both reach, behind where it is behind it at every one (`pairAt`'s
 * nearest) — so what lies inside a wall behind the surface seen is hidden by
 * it. In front of that surface at some columns and behind it at others, it
 * reaches into the wall: the segment decides as it did alone, wholly in front
 * or wholly behind, weakly and over those columns only (`exact`). Without a
 * wall, the segment's line (`planVerdict`'s `nearest`).
 */
export function faceVerdict(rs: PlanRings, rings: PlanRings, c0: number, c1: number): -1 | 0 | 1 {
  if (rs.length < 2) return planVerdict(rs[0], rings, c0, c1, true);
  open();
  for (const r of rings) if (!ringPair(r, rs[1], c0, c1, true)) break;
  const v = close(true);
  if (v || vNf || vNb) return v; // decided, on the surface, or nothing over those columns
  const u = planVerdict(rs[0], rings, c0, c1);
  if (u) { margin -= INTERPENETRATING; exact = false; }
  return u;
}

/** A verdict being found: wholly in front so far, wholly behind so far, its
 *  nearest depth in front and behind so far (`nearest`), anything read, and
 *  the least clearance each way. */
let vFront = true, vBehind = true, vNf = true, vNb = true, vAny = false, vMf = Infinity, vMb = Infinity, vMnf = Infinity, vMnb = Infinity;
function open(): void { vFront = vBehind = vNf = vNb = true; vAny = false; vMf = vMb = vMnf = vMnb = Infinity; }
function close(nearest: boolean): -1 | 0 | 1 {
  const f = nearest ? vNf : vFront, b = nearest ? vNb : vBehind;
  if (!vAny || f === b) return 0;
  // Wholly apart, the clearance; by the nearest depths alone (the plans
  // interpenetrate) weaker than any of those, as the weakest turn is chosen.
  margin = (f ? vFront : vBehind) ? Math.max(0, f ? vMf : vMb) : Math.max(0, f ? vMnf : vMnb) - INTERPENETRATING;
  return f ? 1 : -1;
}
/** Can the verdict being found still be one? */
const undecided = (nearest: boolean): boolean => (nearest ? vNf || vNb : vFront || vBehind);

/** Ring `r` against the line d = da + slope·(t − ta) at column t. */
function lineAt(r: ArrayLike<number>, t: number, ta: number, da: number, slope: number): void {
  if (!reach(r, t)) return;
  vAny = true;
  const ds = da + slope * (t - ta);
  if (rHi > ds + EPS) vFront = false; else if (ds - rHi < vMf) vMf = ds - rHi;
  if (rLo < ds - EPS) vBehind = vNb = false; else if (rLo - ds < vMb) vMb = vMnb = rLo - ds;
  if (rLo > ds + EPS) vNf = false; else if (ds - rLo < vMnf) vMnf = ds - rLo;
}

/** Ring `ra` against ring `rb` at column t. */
function pairAt(ra: ArrayLike<number>, rb: ArrayLike<number>, t: number): void {
  if (!reach(ra, t)) return;
  const aLo = rLo, aHi = rHi;
  if (!reach(rb, t)) return;
  vAny = true;
  if (aHi > rLo + EPS) vFront = false; else if (rLo - aHi < vMf) vMf = rLo - aHi;
  if (rHi > aLo + EPS) vBehind = false; else if (aLo - rHi < vMb) vMb = aLo - rHi;
  if (aLo > rLo + EPS) vNf = false; else if (rLo - aLo < vMnf) vMnf = rLo - aLo;
  if (aLo < rLo - EPS) vNb = false; else if (aLo - rLo < vMnb) vMnb = aLo - rLo;
}

const ringT0 = (r: ArrayLike<number>): number => { let a = Infinity; for (let i = 0; i < r.length; i += 2) if (r[i] < a) a = r[i]; return a; };
const ringT1 = (r: ArrayLike<number>): number => { let b = -Infinity; for (let i = 0; i < r.length; i += 2) if (r[i] > b) b = r[i]; return b; };

/**
 * How plan `a` stands against plan `b` over the columns [c0, c1]: 1 wholly in
 * front of it at every column both reach there, −1 wholly behind (touching is
 * either), 0 neither. With `nearest`, by their nearest depths at each column:
 * in front where a's stands in front of b's at every one, behind where it
 * stands behind (two opaque prisms: the one whose front is nearer hides the
 * other there). Exact, as `planVerdict`: at a column each ring's nearest and
 * farthest depths are piecewise linear in t, breaking only at its own points'
 * columns, so how far one stands from the other is found at both rings'
 * points' columns and the ends. Two DISJOINT CONVEX plans always have one:
 * over the columns they share neither can pass behind the other without
 * crossing it.
 */
export function ringsVerdict(a: PlanRings, b: PlanRings, c0: number, c1: number, nearest = false): -1 | 0 | 1 {
  open();
  for (const ra of a) for (const rb of b) if (!ringPair(ra, rb, c0, c1, nearest)) return 0;
  return close(nearest);
}

/** Ring `ra` against ring `rb` over the columns both reach in [c0, c1], into
 *  the verdict being found: can it still be one? */
function ringPair(ra: ArrayLike<number>, rb: ArrayLike<number>, c0: number, c1: number, nearest: boolean): boolean {
  const lo = Math.max(c0, ringT0(ra), ringT0(rb)), hi = Math.min(c1, ringT1(ra), ringT1(rb));
  if (!(hi >= lo)) return true;
  pairAt(ra, rb, lo);
  if (hi > lo) pairAt(ra, rb, hi);
  for (let i = 0; i < ra.length && undecided(nearest); i += 2) if (ra[i] > lo && ra[i] < hi) pairAt(ra, rb, ra[i]);
  for (let i = 0; i < rb.length && undecided(nearest); i += 2) if (rb[i] > lo && rb[i] < hi) pairAt(ra, rb, rb[i]);
  return undecided(nearest);
}

/** A poly's ring, unless it is a rectangle. */
function ringOf(R: readonly SecPt[]): readonly SecPt[] | null {
  if (R.length !== 4) return R;
  for (let i = 0; i < 4; i++) { const p = R[i], q = R[(i + 1) & 3]; if (p.t !== q.t && p.h !== q.h) return R; }
  return null;
}

/** Is (t, h) inside ring R, clear of its edges by more than EPS? */
function strictlyInside(R: readonly SecPt[], t: number, h: number): boolean {
  let c = false;
  for (let i = 0, j = R.length - 1; i < R.length; j = i++) {
    const p = R[j], q = R[i], dt = q.t - p.t, dh = q.h - p.h, L2 = dt * dt + dh * dh;
    const w = L2 > 0 ? Math.max(0, Math.min(1, ((t - p.t) * dt + (h - p.h) * dh) / L2)) : 0;
    if (Math.hypot(t - p.t - dt * w, h - p.h - dh * w) <= EPS) return false;
    if ((p.h > h) !== (q.h > h) && t < p.t + (dt * (h - p.h)) / dh) c = !c;
  }
  return c;
}

/** A box's ring, refilled for each box `viewsShare` reads (one at a time). */
const BOX_RING: SecPt[] = [{ t: 0, h: 0 }, { t: 0, h: 0 }, { t: 0, h: 0 }, { t: 0, h: 0 }];

/** Box i as a ring (`BOX_RING`). */
function boxRing(box: Float64Array, i: number): readonly SecPt[] {
  const t0 = box[4 * i], t1 = box[4 * i + 1], h0 = box[4 * i + 2], h1 = box[4 * i + 3], R = BOX_RING;
  R[0].t = t0; R[0].h = h0; R[1].t = t1; R[1].h = h0; R[2].t = t1; R[2].h = h1; R[3].t = t0; R[3].h = h1;
  return R;
}

/** The heights where a ring crosses column t, sorted, into `out`: inside between each pair (t is no point's column). */
function crossings(R: readonly SecPt[], t: number, out: number[]): number[] {
  out.length = 0;
  for (let i = 0, j = R.length - 1; i < R.length; j = i++) {
    const p = R[j], q = R[i];
    if ((p.t < t) !== (q.t < t)) out.push(p.h + ((q.h - p.h) * (t - p.t)) / (q.t - p.t));
  }
  return out.sort((x, y) => x - y);
}

/** `viewsShare`'s columns and crossings, kept between calls. */
const TS: number[] = [], CA: number[] = [], CB: number[] = [];

/** Is the middle of box k, if it has area, strictly inside ring R? Then area of both lies round it. */
const middleIn = (box: Float64Array, k: number, R: readonly SecPt[]): boolean => box[4 * k + 1] - box[4 * k] > EPS && box[4 * k + 3] - box[4 * k + 2] > EPS &&
  strictlyInside(R, (box[4 * k] + box[4 * k + 1]) / 2, (box[4 * k + 2] + box[4 * k + 3]) / 2);

/** The edge pairs `viewsShare` has compared — the work it did. */
let shareSteps = 0;

/**
 * Do two calls whose boxes meet share AREA in the view? A vedge or a line has
 * none: it is taken to (its box meets the other's) — conservative —, as are
 * two rectangles (exact: their boxes are them). Exact for two areas otherwise:
 * in each slab between the columns of their points and of their edges'
 * crossings the order of their edges is fixed, so what they share at its
 * middle column is what they share all across it.
 */
function viewsShare(calls: readonly SecCall[], cls: Int32Array, box: Float64Array, i: number, j: number): boolean {
  const ci = cls[i], cj = cls[j];
  if (ci === NO_AREA || cj === NO_AREA || (ci === BOXED && cj === BOXED)) return true;
  // One of the two is a ring: a box's middle inside it settles it at once.
  const ra = ci === RING ? (calls[i].a[0] as readonly SecPt[]) : null, rb = cj === RING ? (calls[j].a[0] as readonly SecPt[]) : null;
  shareSteps += (ra ? ra.length : 4) + (rb ? rb.length : 4);
  if ((!ra && middleIn(box, i, rb!)) || (!rb && middleIn(box, j, ra!))) return true;
  const A = ra ?? boxRing(box, i), B = rb ?? boxRing(box, j);
  shareSteps += A.length * B.length;
  const lo = Math.max(box[4 * i], box[4 * j]), hi = Math.min(box[4 * i + 1], box[4 * j + 1]);
  const ts = TS;
  ts.length = 0;
  ts.push(lo, hi);
  for (const p of A) if (p.t > lo && p.t < hi) ts.push(p.t);
  for (const p of B) if (p.t > lo && p.t < hi) ts.push(p.t);
  for (let e = 0, f = A.length - 1; e < A.length; f = e++) {
    const p = A[f], q = A[e];
    if (Math.abs(q.t - p.t) <= EPS) continue;
    for (let k = 0, m = B.length - 1; k < B.length; m = k++) {
      const r = B[m], s = B[k];
      if (Math.abs(s.t - r.t) <= EPS) continue;
      const x0 = Math.max(Math.min(p.t, q.t), Math.min(r.t, s.t), lo), x1 = Math.min(Math.max(p.t, q.t), Math.max(r.t, s.t), hi);
      if (!(x1 > x0)) continue;
      const g0 = p.h + ((q.h - p.h) * (x0 - p.t)) / (q.t - p.t) - r.h - ((s.h - r.h) * (x0 - r.t)) / (s.t - r.t);
      const g1 = p.h + ((q.h - p.h) * (x1 - p.t)) / (q.t - p.t) - r.h - ((s.h - r.h) * (x1 - r.t)) / (s.t - r.t);
      if ((g0 < 0 && g1 > 0) || (g0 > 0 && g1 < 0)) ts.push(x0 + ((x1 - x0) * g0) / (g0 - g1));
    }
  }
  ts.sort((x, y) => x - y);
  for (let k = 1; k < ts.length; k++) {
    if (!(ts[k] - ts[k - 1] > EPS)) continue;
    const m = (ts[k] + ts[k - 1]) / 2, ca = crossings(A, m, CA), cb = crossings(B, m, CB);
    shareSteps += A.length + B.length;
    for (let e = 0; e + 1 < ca.length; e += 2) for (let f = 0; f + 1 < cb.length; f += 2) {
      if (Math.min(ca[e + 1], cb[f + 1]) - Math.max(ca[e], cb[f]) > EPS) return true;
    }
  }
  return false;
}

/** A binary heap of numbers, least first. */
class MinHeap {
  private a: number[] = [];
  get size(): number { return this.a.length; }
  clear(): void { this.a.length = 0; }
  push(x: number): void {
    const a = this.a;
    let i = a.push(x) - 1;
    while (i > 0) { const p = (i - 1) >> 1; if (a[p] <= x) break; a[i] = a[p]; i = p; }
    a[i] = x;
  }
  pop(): number {
    const a = this.a, top = a[0], x = a.pop()!;
    if (a.length) {
      let i = 0;
      for (;;) {
        let c = 2 * i + 1;
        if (c >= a.length) break;
        if (c + 1 < a.length && a[c + 1] < a[c]) c++;
        if (a[c] >= x) break;
        a[i] = a[c]; i = c;
      }
      a[i] = x;
    }
    return top;
  }
}

/* What is known of a set of calls with a plan — a group's, an object's, its
 * faces' and walls' — is a SPAN, at offset k of a `Float64Array`: the columns
 * its plans span (k, k + 1), their depths (k + 2, k + 3) and its calls' tags
 * (k + 4, k + 5; none yet: ∞, −∞). */

/** Does all of the span at x paint before all of f's (tagged farther), or after? */
const before = (s: Float64Array, x: number, f: number): boolean => s[x + 4] > s[f + 5];
const after = (s: Float64Array, x: number, f: number): boolean => s[x + 5] < s[f + 4];
/** Is everything of x behind all of f and painted before it, or in front and after? Then no pair of theirs turns. */
const settled = (s: Float64Array, f: number, x: number): boolean => (s[x + 2] > s[f + 3] + EPS && before(s, x, f)) || (s[x + 3] < s[f + 2] - EPS && after(s, x, f));
const columnsMeet = (s: Float64Array, a: number, b: number): boolean => Math.min(s[a + 1], s[b + 1]) - Math.max(s[a], s[b]) >= -EPS;

/** Farther tag first, then the order made — as small numbers (a comparator
 *  returning a difference of doubles boxes one per comparison). */
export const byTag = (a: { d: number; i: number }, b: { d: number; i: number }): number => (a.d > b.d ? -1 : a.d < b.d ? 1 : a.i - b.i);

/** How the last order went (`paintOrderStats`): the calls, the turns found and
 *  dropped, the pairs a cycle held in today's order that their plans turned
 *  (`decided`), the cycles broken, the scan's restarts, the work done against
 *  the budget, whether the tags decided it all, and how many orders were worked
 *  out and reused (`rememberedOrder`) in all. */
export interface PaintOrderStats { calls: number; turns: number; dropped: number; decided: number; cycles: number; restarts: number; work: number; budget: number; tags: boolean; computed: number; reused: number }
const STATS: PaintOrderStats = { calls: 0, turns: 0, dropped: 0, decided: 0, cycles: 0, restarts: 0, work: 0, budget: 0, tags: false, computed: 0, reused: 0 };

/** Test seam: how the last order went, and the orders worked out and reused
 *  — with `forget`, the orders kept are dropped first. */
export function paintOrderStats(forget = false): PaintOrderStats {
  if (forget) { MEMO.clear(); memoHeld = 0; }
  return { ...STATS };
}

/** The budget (work) and the cycles one order may break (test seams). */
export interface PaintOrderOptions { budget?: number; cycles?: number }

/** A call's state in the scan: waiting, waiting to be looked at again (in the
 *  heap), ready (in the heap), painted. */
const WAITING = 1, LOOK = 2, READY = 3, PAINTED = 4;

/** The heap the scan paints its ready calls from, kept between orders. */
const HEAP = new MinHeap();

/**
 * The order to paint `calls` in. They are sorted into today's order — farthest
 * tag first, ties in the order made (`i`) — and that is the order (null)
 * unless a call of a FACE or of a drawn WALL and a call of another object with
 * a plan (their `groups`) share area in the view and their plans say, over the
 * columns they share, that it runs the other way round: a TURN. Then every two
 * calls that share area keep today's order but the turns, and each call is
 * painted as soon as what must precede it is, the earliest in today's order
 * first (Kahn) — the order returned, as positions in the sorted calls.
 *
 * The pairs compared are a sweep's in t: each object with a face or a wall
 * against the objects whose columns meet its own, a pair whose plans and tags
 * already agree settled at once. A call that waits — for a turn, or for a call
 * that waits and shares area with it — watches ONE of the waiting calls it
 * shares area with, the latest in today's order, found in the cells of a grid
 * over the view (in t and in h: calls stacked one above another meet in none),
 * and looks again once that one is painted, so a crowd of calls waiting for
 * one face costs a look each, not one per pair. A cycle the turns close with
 * today's order is broken first at a pair it holds in today's order whose
 * plans say the other way round — turned like the rest —, else at its weakest
 * turn (the least clearance; the latest of equals), one at a time, the rest
 * kept. Every step is counted, and past the budget the part of the drawing
 * that cost it keeps its tags: the pairs left unswept, the turns of the
 * objects whose waiting calls the scan spent it on (and it starts again), the
 * turns among the calls a cycle holds.
 */
export function paintOrder<P>(calls: PaintCall[], groups: readonly PaintGroup<P>[], planOf: (p: P) => PlanRings, opts: PaintOrderOptions = {}): number[] | null {
  calls.sort(byTag);
  const n = calls.length, G = groups.length;
  const budget = opts.budget ?? BUDGET + PER_CALL * n, maxCycles = opts.cycles ?? CYCLES;
  Object.assign(STATS, { calls: n, turns: 0, dropped: 0, decided: 0, cycles: 0, restarts: 0, work: 0, budget, tags: true });
  STATS.computed++;
  // Anything to decide takes a face or a wall, and a group of another object.
  let special = false, many = false, objects = 0;
  for (const g of groups) { if (g.face || g.wall) special = true; if (g.o !== groups[0].o) many = true; if (g.o >= objects) objects = g.o + 1; }
  if (!special || !many) return null;
  // The spans: each group's at 6·g, then each object's — all its
  // groups', and its faces' and walls' — at `oAll(o)` and `oSpec(o)`; whether
  // it has a face or a wall, and its groups with calls (a list through `gNext`).
  const SP = floats(6, 6 * G + 12 * objects);
  const oAll = (o: number): number => 6 * G + 12 * o, oSpec = (o: number): number => 6 * G + 12 * o + 6;
  const spec = ints(29, objects), gFirst = ints(30, objects, -1), gLast = ints(31, objects, -1), gNext = ints(32, G, -1);
  for (let k = 6 * G; k < SP.length; k += 2) { SP[k] = Infinity; SP[k + 1] = -Infinity; }
  const widen = (o: number, g: number): void => {
    for (let k = 0; k < 6; k += 2) {
      if (SP[g + k] < SP[o + k]) SP[o + k] = SP[g + k];
      if (SP[g + k + 1] > SP[o + k + 1]) SP[o + k + 1] = SP[g + k + 1];
    }
  };
  for (let g = 0; g < G; g++) {
    const gr = groups[g], k = 6 * g;
    SP[k] = gr.t0; SP[k + 1] = gr.t1; SP[k + 2] = gr.d0; SP[k + 3] = gr.d1; SP[k + 4] = gr.z0; SP[k + 5] = gr.z1;
    if (!(gr.z1 >= gr.z0)) continue; // no call
    const o = gr.o;
    widen(oAll(o), k);
    if (gr.face || gr.wall) { widen(oSpec(o), k); spec[o] = 1; }
    if (gLast[o] >= 0) gNext[gLast[o]] = g; else gFirst[o] = g;
    gLast[o] = g;
  }
  // The work done, all told, and where the phase under way must stop (`stop`).
  let work = 0, limit = budget / 2, stop = false;
  shareSteps = 0;
  const done = (o: number[] | null): number[] | null => { STATS.work = work + shareSteps; STATS.tags = !o; return o; };
  // Each group's calls by position, and the calls' boxes — once a pair of groups needs them.
  let at: Int32Array = new Int32Array(0), byGroup = at, boxed = at, cls = at, box: Float64Array = new Float64Array(0);
  const callsOf = (): void => {
    if (at.length) return;
    at = ints(0, G + 1); byGroup = ints(1, n); box = floats(0, 4 * n); boxed = ints(2, n); cls = ints(23, n);
    for (let k = 0; k < n; k++) { const g = calls[k].g; if (g >= 0) at[g + 1]++; }
    for (let g = 0; g < G; g++) at[g + 1] += at[g];
    const fill = ints(18, G);
    fill.set(at.subarray(0, G));
    for (let k = 0; k < n; k++) { const g = calls[k].g; if (g >= 0) byGroup[fill[g]++] = k; }
  };
  const boxOf = (k: number): void => { if (!boxed[k]) { boxed[k] = 1; boxInto(box, cls, k, calls[k]); } };
  const plans: Array<PlanRings | undefined> = new Array(G);
  const ringsOf = (g: number): PlanRings => (plans[g] ??= planOf(groups[g].plan));
  const sizes = floats(2, G, -1);
  const sizeOf = (g: number): number => { if (sizes[g] < 0) { let s = 0; for (const x of ringsOf(g)) s += x.length >> 1; sizes[g] = s; } return sizes[g]; };
  // The objects joined by turns, as sets (union–find), and the work charged to each.
  const parent = ints(3, objects, -1), charge = floats(1, objects);
  const find = (o: number): number => { let r = o; while (parent[r] >= 0) r = parent[r]; while (parent[o] >= 0) { const p = parent[o]; parent[o] = r; o = p; } return r; };
  const join = (a: number, b: number): number => { a = find(a); b = find(b); if (a === b) return a; if (a > b) [a, b] = [b, a]; parent[b] = a; charge[a] += charge[b]; return a; };
  const objOf = (k: number): number => groups[calls[k].g].o;
  // The turns: who is painted first, who after, the clearance their verdict found, whether dropped.
  let tFrom = keepInts(24, 256), tTo = keepInts(25, 256), tStr = keepFloats(3, 256), tDrop = keepInts(19, 256), T = 0;
  const addTurn = (from: number, to: number, str: number): number => {
    if (T === tFrom.length) { tFrom = keepInts(24, 2 * T); tTo = keepInts(25, 2 * T); tStr = keepFloats(3, 2 * T); tDrop = keepInts(19, 2 * T); }
    tFrom[T] = from; tTo[T] = to; tStr[T] = str; tDrop[T] = 0;
    return T++;
  };
  /** How plan X stands against S's: 1 in front of it, −1 behind, 0 neither —
   *  with `nearest`, against a footprint by their nearest depths too. */
  const verdict = (s: number, x: number, c0: number, c1: number, nearest = false): -1 | 0 | 1 => {
    const S = groups[s], X = groups[x], rs = ringsOf(s);
    work++;
    exact = true;
    // At once where X's depths all lie on one side of those S stands at over
    // the columns: its line's there, its wall's, or its whole plan's.
    let lo = S.d0, hi = S.d1;
    if (S.face) {
      if (!(c1 >= c0)) return 0;
      const a = Math.max(S.t0, Math.min(S.t1, c0)), b = Math.max(S.t0, Math.min(S.t1, c1));
      const seg = rs[0], ta = seg[0], da = seg[1], tb = seg[2], db = seg[3];
      if (!(Math.abs(tb - ta) > EPS)) return 0;
      const la = da + ((db - da) * (a - ta)) / (tb - ta), lb = da + ((db - da) * (b - ta)) / (tb - ta);
      lo = Math.min(la, lb); hi = Math.max(la, lb);
      if (rs.length > 1) {
        // The wall's depths over the columns, read where they break (its corners' columns) and at the ends.
        const w = rs[1];
        spanLo = lo; spanHi = hi;
        widenAt(w, a); widenAt(w, b);
        for (let k = 0; k < w.length; k += 2) if (w[k] > a && w[k] < b) widenAt(w, w[k]);
        lo = spanLo; hi = spanHi;
      }
    }
    if (X.d1 < lo - EPS) { margin = lo - X.d1; return 1; }
    if (X.d0 > hi + EPS) { margin = X.d0 - hi; return -1; }
    const rx = ringsOf(x);
    work += sizeOf(s) * sizeOf(x);
    return S.face ? faceVerdict(rs, rx, c0, c1) : ringsVerdict(rx, rs, c0, c1, nearest);
  };
  /** How group A's plan stands against B's over the columns, by their nearest
   *  depths (opaque prisms: whose front is nearer hides the other there): 1 in
   *  front, −1 behind, 0 neither. */
  const against = (a: number, b: number, c0: number, c1: number): -1 | 0 | 1 => (groups[a].face && !groups[b].face ? (-verdict(a, b, c0, c1, true) as -1 | 0 | 1) : verdict(b, a, c0, c1, true));
  /** Face or wall group `sg` against group `xg` of another object. */
  const decide = (sg: number, xg: number): void => {
    const S = groups[sg], X = groups[xg];
    work++;
    if (!columnsMeet(SP, 6 * sg, 6 * xg) || settled(SP, 6 * sg, 6 * xg)) return;
    // The verdict over all the columns both plans span holds over any of them, if `exact`.
    let whole = verdict(sg, xg, Math.max(S.t0, X.t0), Math.min(S.t1, X.t1));
    const wm = margin;
    if (!exact) whole = 0;
    if (whole !== 0 && (whole > 0 ? after(SP, 6 * xg, 6 * sg) : before(SP, 6 * xg, 6 * sg))) return;
    callsOf();
    for (let a = at[sg]; a < at[sg + 1]; a++) {
      const f = byGroup[a];
      boxOf(f);
      for (let b = at[xg]; b < at[xg + 1]; b++) {
        const x = byGroup[b];
        boxOf(x);
        if (++work + shareSteps > limit) { stop = true; return; }
        if (!boxesMeet(box, f, x)) continue;
        const v = whole || verdict(sg, xg, Math.max(box[4 * f], box[4 * x]), Math.min(box[4 * f + 1], box[4 * x + 1]));
        if (v === 0 || (v > 0) === (x > f) || !viewsShare(calls, cls, box, f, x)) continue;
        addTurn(v > 0 ? f : x, v > 0 ? x : f, whole ? wm : margin);
        join(S.o, X.o);
      }
    }
  };
  /** Object P's faces and walls against object Q's groups — with `first` false
   *  not those already met from Q's side: two faces, a face and a wall, two
   *  walls meet once. */
  const side = (P: number, Q: number, first: boolean): void => {
    if (!spec[P] || settled(SP, oSpec(P), oAll(Q))) return;
    for (let sg = gFirst[P]; sg >= 0; sg = gNext[sg]) {
      const S = groups[sg];
      if (!(S.face || S.wall) || !columnsMeet(SP, 6 * sg, oAll(Q)) || settled(SP, 6 * sg, oAll(Q))) continue;
      for (let xg = gFirst[Q]; xg >= 0; xg = gNext[xg]) {
        const X = groups[xg];
        if (S.face ? !first && X.face : X.face || (!first && !!X.wall)) continue;
        decide(sg, xg);
        if (stop) return;
      }
    }
  };
  // The objects whose columns meet, swept in t: each against those with a face
  // or a wall, and each with one against all — the pairs left past half the
  // budget keep their tags.
  let nl = 0;
  const list = ints(33, objects);
  for (let o = 0; o < objects; o++) if (gFirst[o] >= 0) list[nl++] = o;
  list.subarray(0, nl).sort((a, b) => (SP[oAll(a)] < SP[oAll(b)] ? -1 : SP[oAll(a)] > SP[oAll(b)] ? 1 : a - b));
  const activeAll: number[] = [], activeSpec: number[] = [];
  sweep: for (let l = 0; l < nl; l++) {
    const A = list[l], act = spec[A] ? activeAll : activeSpec;
    let w = 0;
    for (let k = 0; k < act.length; k++) {
      const B = act[k];
      if (SP[oAll(B) + 1] < SP[oAll(A)] - EPS) continue;
      act[w++] = B;
      if (++work + shareSteps > limit) break sweep;
      side(A, B, true);
      side(B, A, false);
      if (stop) break sweep;
    }
    if (w < act.length) act.length = w;
    activeAll.push(A);
    if (spec[A]) activeSpec.push(A);
  }
  STATS.turns = T;
  if (!T) return done(null);
  // Each call's turns, in the order found: those it waits for and those waiting
  // for it, as lists through the turns (a pair a cycle turns is added to them).
  const predHead = ints(4, n, -1), predTail = ints(5, n, -1), succHead = ints(6, n, -1), succTail = ints(7, n, -1);
  let predNext = keepInts(26, T), succNext = keepInts(27, T);
  const link = (t: number): void => {
    if (t >= predNext.length) { predNext = keepInts(26, 2 * t); succNext = keepInts(27, 2 * t); }
    const a = tFrom[t], b = tTo[t];
    predNext[t] = succNext[t] = -1;
    if (predTail[b] >= 0) predNext[predTail[b]] = t; else predHead[b] = t;
    predTail[b] = t;
    if (succTail[a] >= 0) succNext[succTail[a]] = t; else succHead[a] = t;
    succTail[a] = t;
  };
  for (let t = 0; t < T; t++) link(t);
  // The calls waiting, by the cells of a grid over the view their boxes cover:
  // in each a list in today's order, through nodes (a call's consecutive from
  // `firstNode`, column after column), a painted call unlinked from all of
  // them. A cell is about a middling call's size in t and in h, at most
  // max(64, n) of them; a column no waiting call is in is passed at once.
  callsOf();
  let T0 = Infinity, T1 = -Infinity, H0 = Infinity, H1 = -Infinity, nw = 0, nh = 0;
  const ws = floats(4, n), hs = floats(5, n);
  for (let k = 0; k < n; k++) {
    boxOf(k);
    const a = box[4 * k], b = box[4 * k + 1], c = box[4 * k + 2], d = box[4 * k + 3];
    if (a < T0) T0 = a;
    if (b > T1) T1 = b;
    if (c < H0) H0 = c;
    if (d > H1) H1 = d;
    if (b - a > EPS) ws[nw++] = b - a;
    if (d - c > EPS) hs[nh++] = d - c;
  }
  const cells = Math.max(64, n);
  let kt = Math.max(1, (T1 - T0) / (nw ? ws.subarray(0, nw).sort()[nw >> 1] : T1 - T0) || 1);
  let kh = Math.max(1, (H1 - H0) / (nh ? hs.subarray(0, nh).sort()[nh >> 1] : H1 - H0) || 1);
  if (kt * kh > cells) { const s = Math.sqrt(cells / (kt * kh)); kt *= s; kh *= s; }
  const KT = Math.max(1, Math.min(cells, Math.floor(kt))), KH = Math.max(1, Math.min(Math.floor(cells / KT), Math.floor(kh)));
  const wT = (T1 - T0) / KT || 1, wH = (H1 - H0) / KH || 1;
  const binT = (t: number): number => Math.max(0, Math.min(KT - 1, Math.floor((t - T0) / wT)));
  const binH = (h: number): number => Math.max(0, Math.min(KH - 1, Math.floor((h - H0) / wH)));
  // The scan's state: each call's state, its turns left, the call it watches
  // and those watching it (a list through `wNext` / `wPrev`), and the objects
  // its waiting is charged to.
  const state = ints(8, n), tcount = ints(9, n), watch = ints(10, n, -1), wHead = ints(11, n, -1), wNext = ints(12, n, -1), wPrev = ints(20, n, -1), cause = ints(13, n, -1);
  const tail = ints(22, KT * KH, -1), colCount = ints(21, KT), firstNode = ints(15, n, -1);
  let nPrev = NODES[0], nNext = NODES[1], nCall = NODES[2], nodes = 0;
  let order: number[] = [], held = 0;
  const heap = HEAP;
  // Per look, a stamp on the calls looked at and on those turned after the one looking.
  const seen = ints(16, n), turnedTo = ints(17, n);
  let stamp = 0;
  // The walk round a cycle (below): the calls on it, by place (`mark`), and the
  // first place a call painted since has left.
  const mark = ints(14, n, -1), path: number[] = [], via: number[] = [];
  let left = 0;
  /** The latest waiting call before c in today's order that shares area with
   *  it and is not turned after it, or −1. */
  const query = (c: number): number => {
    const s = ++stamp, w0 = work + shareSteps;
    // None, at once, when every other call waiting is turned after it.
    let after = 0;
    for (let x = succHead[c]; x >= 0; x = succNext[x]) {
      work++;
      const q = tTo[x];
      if (tDrop[x] || turnedTo[q] === s) continue;
      turnedTo[q] = s;
      if (state[q] !== PAINTED) after++;
    }
    if (after >= held - (firstNode[c] >= 0 ? 1 : 0)) return -1;
    let best = -1, first = -1;
    const q0 = binT(box[4 * c]), q1 = binT(box[4 * c + 1]), r0 = binH(box[4 * c + 2]), r1 = binH(box[4 * c + 3]);
    const rows = r1 - r0 + 1, own = firstNode[c];
    look: for (let q = q0; q <= q1; q++) {
      if (!colCount[q]) continue;
      for (let r = r0; r <= r1; r++) {
        if (++work + shareSteps > limit) { stop = true; break look; }
        for (let node = own >= 0 ? nPrev[own + (q - q0) * rows + r - r0] : tail[q * KH + r]; node >= 0; node = nPrev[node]) {
          const j = nCall[node];
          if (j <= best) break;
          if (++work + shareSteps > limit) { stop = true; break look; }
          if (seen[j] === s) continue;
          seen[j] = s;
          if (first < 0) first = j;
          if (turnedTo[j] === s || !boxesMeet(box, c, j) || !viewsShare(calls, cls, box, j, c)) continue;
          best = j;
          break;
        }
      }
    }
    if (first >= 0) charge[find(cause[first])] += work + shareSteps - w0;
    return best;
  };
  /** c waits: linked into the cells it covers — the nodes it took. */
  const wait = (c: number): number => {
    state[c] = WAITING; held++;
    const q0 = binT(box[4 * c]), q1 = binT(box[4 * c + 1]), r0 = binH(box[4 * c + 2]), r1 = binH(box[4 * c + 3]);
    const rows = r1 - r0 + 1, m = (q1 - q0 + 1) * rows;
    work += m;
    if (nodes + m > nPrev.length) {
      for (let k = 0; k < 3; k++) { const a = NODES[k]; NODES[k] = new Int32Array(2 * a.length + m); NODES[k].set(a); }
      [nPrev, nNext, nCall] = NODES;
    }
    firstNode[c] = nodes;
    for (let q = q0; q <= q1; q++) {
      colCount[q] += rows;
      for (let r = r0; r <= r1; r++, nodes++) {
        const cell = q * KH + r;
        nCall[nodes] = c; nPrev[nodes] = tail[cell]; nNext[nodes] = -1;
        if (tail[cell] >= 0) nNext[tail[cell]] = nodes;
        tail[cell] = nodes;
      }
    }
    return m;
  };
  const watchOn = (c: number, w: number): void => {
    watch[c] = w; wPrev[c] = -1; wNext[c] = wHead[w];
    if (wHead[w] >= 0) wPrev[wHead[w]] = c;
    wHead[w] = c;
    const own = calls[c].g >= 0 ? objOf(c) : -1;
    if (own < 0) cause[c] = cause[w]; else { join(own, cause[w]); cause[c] = own; }
  };
  const unwatch = (c: number): void => {
    const w = watch[c];
    if (w < 0) return;
    if (wPrev[c] >= 0) wNext[wPrev[c]] = wNext[c]; else if (wHead[w] === c) wHead[w] = wNext[c];
    if (wNext[c] >= 0) wPrev[wNext[c]] = wPrev[c];
    watch[c] = -1;
  };
  /** c is looked at again once the calls before it in the heap are. */
  const again = (c: number): void => { state[c] = LOOK; heap.push(c); };
  const evaluate = (c: number): void => {
    unwatch(c);
    const w = query(c);
    if (stop) return;
    if (w >= 0) watchOn(c, w); else { state[c] = READY; heap.push(c); }
  };
  const paint = (u: number): void => {
    const was = state[u];
    state[u] = PAINTED;
    order.push(u);
    if (mark[u] >= 0 && mark[u] < left) left = mark[u];
    if (was) {
      held--;
      const q0 = binT(box[4 * u]), q1 = binT(box[4 * u + 1]), r0 = binH(box[4 * u + 2]), r1 = binH(box[4 * u + 3]), rows = r1 - r0 + 1;
      for (let q = q0, node = firstNode[u]; q <= q1; q++) {
        colCount[q] -= rows;
        for (let r = r0; r <= r1; r++, node++) {
          const p = nPrev[node], x = nNext[node];
          if (p >= 0) nNext[p] = x;
          if (x >= 0) nPrev[x] = p; else tail[q * KH + r] = p;
        }
      }
    }
    for (let t = succHead[u]; t >= 0; t = succNext[t]) {
      if (tDrop[t]) continue;
      const q = tTo[t];
      if (++work + shareSteps > limit) { stop = true; return; }
      if (--tcount[q] === 0 && state[q] === WAITING) again(q);
    }
    for (let c = wHead[u]; c >= 0;) {
      const nx = wNext[c];
      if (watch[c] === u) {
        watch[c] = -1;
        if (state[c] === WAITING && tcount[c] === 0) again(c);
      }
      c = nx;
    }
    wHead[u] = -1;
  };
  const drain = (): void => {
    while (heap.size && !stop) {
      const c = heap.pop();
      if (state[c] === LOOK) { state[c] = WAITING; evaluate(c); } else paint(c);
    }
  };
  /** Kahn over today's order; 'budget' once the work passes the budget. */
  const scan = (): 'done' | 'stuck' | 'budget' => {
    state.fill(0); watch.fill(-1); wHead.fill(-1); cause.fill(-1); tcount.fill(0); colCount.fill(0);
    tail.fill(-1); firstNode.fill(-1); nodes = 0;
    order = []; held = 0; heap.clear(); charge.fill(0);
    limit = work + shareSteps + budget; stop = false;
    for (let t = 0; t < T; t++) if (!tDrop[t]) tcount[tTo[t]]++;
    for (let i = 0; i < n; i++) {
      drain();
      if (stop || ++work + shareSteps > limit) return 'budget';
      if (tcount[i] > 0) { const m = wait(i); cause[i] = objOf(i); charge[find(cause[i])] += m; continue; }
      if (held) {
        const w = query(i);
        if (stop) return 'budget';
        if (w >= 0) { const m = wait(i); watchOn(i, w); charge[find(cause[i])] += m; continue; }
      }
      paint(i);
    }
    drain();
    return stop ? 'budget' : order.length === n ? 'done' : 'stuck';
  };
  const dropTurn = (t: number): void => {
    tDrop[t] = 1;
    STATS.dropped++;
    const q = tTo[t];
    if (state[q] !== PAINTED && --tcount[q] === 0 && state[q] === WAITING) again(q);
  };
  let r = scan();
  while (r === 'budget') {
    // The objects whose waiting calls cost the most keep their tags.
    let worst = -1;
    for (let o = 0; o < objects; o++) if (parent[o] < 0 && (worst < 0 || charge[o] > charge[worst])) worst = o;
    if (++STATS.restarts > RESTARTS || !(charge[worst] > 0)) return done(null);
    for (let t = 0; t < T; t++) if (!tDrop[t] && find(objOf(tFrom[t])) === worst) { tDrop[t] = 1; STATS.dropped++; }
    r = scan();
  }
  // Cycles: from the first call left waiting, what each waits for, until a call
  // comes round again (`path`, and the turn each waits by — `via`, −1 for a
  // call it shares area with, earlier in today's order). A call watching one of
  // another object their plans put in front of it is turned before it, else
  // the weakest turn on the way round is dropped; the walk goes on from the
  // last call on it still waiting as it was. Half the budget for the cycles,
  // the other half for the calls they leave.
  const cyclesFrom = work + shareSteps;
  limit = cyclesFrom + budget / 2; stop = false;
  const checked = ints(28, n, -1);
  let from = 0;
  const cut = (len: number): void => { for (let k = len; k < path.length; k++) mark[path[k]] = -1; work += path.length - len; path.length = via.length = len; };
  while (order.length < n) {
    while (state[from] === PAINTED) from++;
    if (stop || STATS.cycles >= maxCycles) {
      // The calls left keep today's order among themselves.
      limit = cyclesFrom + budget; stop = false;
      for (let t = 0; t < T; t++) if (!tDrop[t] && state[tTo[t]] !== PAINTED) { tDrop[t] = 1; STATS.dropped++; }
      for (let c = from; c < n && !stop; c++) {
        if (state[c] !== WAITING) continue;
        tcount[c] = 0;
        const w = watch[c];
        if (w >= 0 && state[w] !== PAINTED) continue;
        watch[c] = -1;
        again(c);
      }
      drain();
      if (stop || order.length < n) return done(null);
      break;
    }
    if (left < path.length) cut(left);
    left = n;
    let c = path.length ? path[path.length - 1] : from;
    if (!path.length) { mark[c] = 0; path.push(c); }
    via.length = path.length - 1;
    for (;;) {
      if (++work + shareSteps > limit) { stop = true; break; }
      let b = watch[c], t = -1;
      if (tcount[c] > 0) {
        for (let s = predHead[c]; s >= 0; s = predNext[s]) {
          work++;
          if (!tDrop[s] && state[tFrom[s]] !== PAINTED) { t = s; b = tFrom[s]; break; }
        }
      }
      via.push(t);
      if (b < 0 || mark[b] >= 0) { c = b; break; }
      mark[b] = path.length;
      path.push(b);
      c = b;
    }
    if (stop) continue;
    // A call on it watching one its plan stands behind is turned before that one.
    let flip = -1, fm = 0;
    if (c >= 0) for (let k = mark[c]; k < path.length; k++) {
      if (via[k] >= 0) continue;
      const a = path[k], b = k + 1 < path.length ? path[k + 1] : c, ga = calls[a].g, gb = calls[b].g;
      if (checked[a] === b) continue;
      checked[a] = b;
      if (ga < 0 || gb < 0 || groups[ga].o === groups[gb].o) continue;
      if (against(ga, gb, Math.max(box[4 * a], box[4 * b]), Math.min(box[4 * a + 1], box[4 * b + 1])) < 0) { flip = k; fm = margin; break; }
    }
    if (flip >= 0) {
      const a = path[flip], b = flip + 1 < path.length ? path[flip + 1] : c;
      link(addTurn(a, b, fm));
      STATS.decided++; STATS.cycles++;
      cut(flip + 1);
      unwatch(a);
      tcount[b]++;
      if (tcount[a] === 0) again(a);
      drain();
      continue;
    }
    let weakest = -1, at = -1;
    if (c >= 0) for (let k = mark[c]; k < path.length; k++) {
      const t = via[k];
      work++;
      if (t >= 0 && (weakest < 0 || tStr[t] < tStr[weakest] || (tStr[t] === tStr[weakest] && t > weakest))) { weakest = t; at = k; }
    }
    if (weakest < 0) { STATS.cycles = maxCycles; continue; } // no turn round it: nothing to break but the whole
    STATS.cycles++;
    cut(at + 1);
    dropTurn(weakest);
    drain();
  }
  return done(order);
}

/** Everything an order depends on, as numbers compared exactly: each group's
 *  object, flags and plan, each call's kind, tag, group and the numbers that
 *  place it in the view — not the colours it is painted in, so the canvas and
 *  the snap lists share one. */
export class OrderKey {
  v = new Float64Array(4096);
  n = 0;
  /** Room for m more numbers: the buffer to write them into. */
  room(m: number): Float64Array {
    if (this.n + m > this.v.length) { const w = new Float64Array(Math.max(2 * this.v.length, this.n + m)); w.set(this.v.subarray(0, this.n)); this.v = w; }
    return this.v;
  }
  put(x: number): void { this.room(1)[this.n++] = x; }
  call({ k, a, d, g }: PaintCall): void {
    const kind = KINDS[k];
    switch (kind.shape) {
      case 'ring': case 'path': {
        const pts = a[0] as readonly SecPt[], v = this.room(4 + 2 * pts.length);
        let n = this.n;
        v[n++] = kind.code; v[n++] = d; v[n++] = g; v[n++] = pts.length;
        for (let m = 0; m < pts.length; m++) { v[n++] = pts[m].t; v[n++] = pts[m].h; }
        this.n = n;
        break;
      }
      case 'vedge': { const v = this.room(6); let n = this.n; v[n++] = kind.code; v[n++] = d; v[n++] = g; v[n++] = a[0] as number; v[n++] = a[1] as number; v[n++] = a[2] as number; this.n = n; break; }
      case 'quad': { const v = this.room(7); let n = this.n; v[n++] = kind.code; v[n++] = d; v[n++] = g; v[n++] = a[0] as number; v[n++] = a[1] as number; v[n++] = a[2] as number; v[n++] = a[3] as number; this.n = n; break; }
      default: { const never: never = kind.shape; throw new Error(`no key for ${String(never)}`); }
    }
  }
  /** Does `kept` hold exactly these numbers? */
  is(kept: Float64Array, n: number): boolean {
    if (n !== this.n) return false;
    const v = this.v;
    for (let k = 0; k < n; k++) if (kept[k] !== v[k]) return false;
    return true;
  }
}

/** An order kept: its key's numbers (`n` of `key`, which may be longer) and
 *  the order, as the calls' `i`. */
interface Kept { key: Float64Array; n: number; order: Int32Array }

/** The orders worked out, by section — the last few keys each, the latest
 *  first —, least recently used out first past `MEMO_BYTES`. */
const MEMO = new Map<string, Kept[]>();
const MEMO_KEYS = 3, MEMO_BYTES = 32e6;
let memoHeld = 0;
const keptBytes = (e: Kept): number => 8 * e.key.length + 4 * e.order.length;

/** The order kept for section `id` under exactly `key`, or null. */
export function rememberedOrder(id: string, key: OrderKey): Int32Array | null {
  const kept = MEMO.get(id);
  if (!kept) return null;
  MEMO.delete(id);
  MEMO.set(id, kept);
  const hit = kept.findIndex((e) => key.is(e.key, e.n));
  if (hit < 0) return null;
  const [e] = kept.splice(hit, 1);
  kept.unshift(e);
  STATS.reused++;
  return e.order;
}

/** Keep `order` (the calls' `i`) for section `id` under `key` — in the
 *  buffers of the entry it puts out, when they are long enough. */
export function rememberOrder(id: string, key: OrderKey, order: ArrayLike<number>): void {
  const kept = MEMO.get(id) ?? [];
  MEMO.delete(id);
  const out = kept.length >= MEMO_KEYS ? kept.pop()! : null;
  if (out) memoHeld -= keptBytes(out);
  const e: Kept = {
    key: out && out.key.length >= key.n ? out.key : new Float64Array(key.n),
    n: key.n,
    order: out && out.order.length === order.length ? out.order : new Int32Array(order.length),
  };
  e.key.set(key.v.subarray(0, key.n));
  e.order.set(order);
  kept.unshift(e);
  memoHeld += keptBytes(e);
  MEMO.set(id, kept);
  for (const [k, v] of MEMO) {
    if (memoHeld <= MEMO_BYTES || k === id) break;
    MEMO.delete(k);
    for (const old of v) memoHeld -= keptBytes(old);
  }
}
